import {Injectable, Type} from '@angular/core';
import {Observable, of} from 'rxjs';
import {catchError, map, mergeMap} from 'rxjs/operators';
import {FollowApiModel} from '../../../../../api/models/follow.api-model';
import {QueryParamsApiModel} from '../../../../../api/models/query-params-api.model';
import {ResultListApiModel} from '../../../../../api/models/result-list.api.model';
import {FollowedSubjectInterface, FollowerListItemInterface} from '../../../../../core/interfaces/follower-item.interface';
import {ProjectRestService} from '../../../../../api/services/project.rest.service';
import {SocialListAdapter, SocialListLoadResult} from '../social-list-adapter.interface';
import {SocialListModalConfig} from '../social-list-modal-config.interface';
import {SocialListType} from '../social-list-type.type';
import {FollowListItemComponent} from '../items/follow-list-item.component';
import {FollowListItemsBuilderService} from '../builders/follow-list-items-builder.service';

type ProjectFollowsSubject = FollowedSubjectInterface;

@Injectable({providedIn: 'root'})
export class ProjectFollowsSocialListAdapter implements SocialListAdapter<ProjectFollowsSubject, never, FollowerListItemInterface> {
  public readonly type: SocialListType = 'project-follows';
  public readonly itemComponent: Type<FollowListItemComponent> = FollowListItemComponent;
  public readonly emptyTranslationKey = 'shared.modal.follow_list.empty';
  public readonly trimmedTranslationKey = 'shared.modal.follow_list.trimmed';
  public readonly titleTranslationKey = 'shared.modal.modal_service.follows.title';

  constructor(
    private readonly projectRS: ProjectRestService,
    private readonly followListItemsBuilder: FollowListItemsBuilderService,
  ) {
  }

  public loadPage(
    config: SocialListModalConfig<ProjectFollowsSubject>,
    page: number,
    pageSize: number
  ): Observable<SocialListLoadResult<FollowerListItemInterface>> {
    const params = new QueryParamsApiModel();
    params.page = page;
    params.limit = pageSize;
    params.pagination = true;

    return this.projectRS.getReceivedFollowsSubResourceCollection(config.subject['@id'], params).pipe(
      map((resultListAM: ResultListApiModel<FollowApiModel>) => resultListAM?.records || []),
      mergeMap((followAMs) => this.followListItemsBuilder.buildItems$(followAMs, config.subject.apiModel).pipe(
        map((items) => ({items, hasMore: followAMs.length >= pageSize}))
      )),
      catchError(() => of({items: [], hasMore: false}))
    );
  }

  public mergeItems(existingItems: FollowerListItemInterface[], incomingItems: FollowerListItemInterface[]): FollowerListItemInterface[] {
    return this.followListItemsBuilder.mergeItems(existingItems, incomingItems);
  }

  public trackBy(index: number, item: FollowerListItemInterface): string {
    return item?.follower?.['@id'] || `${index}`;
  }
}
